import {
  Card,
  CardContent,
  CardHeader,
  CardSubTitle,
  CardTitle,
  Typography,
} from '@growdash/design-system/atoms';
import { Chart, useChart } from '@growdash/design-system/molecules/index.js';
import { cn } from '@growdash/design-system/utils/helpers.js';
import { AvgPriceByMonth } from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { formatNumberWithSign } from '@growdash/growintel/utils/number';
import { useState } from 'react';

type IChartTab = {
  type: 'avgPrices' | 'priceChanges';
  tabValue: string;
  tabLabel: string;
  chartYValues: number[];
  legendLabel?: string;
};

type Props = {
  data?: AvgPriceByMonth[] | null;
  className?: string;
  activeFilters: {
    brandName: string;
    cuisine: string;
  };
};

export default function AvgPrices({
  data,
  activeFilters,
  className = '',
}: Props) {
  const currency = useCurrency();

  const xAxisLabels = data ? data.map((d) => d.scrape_month) : [];
  const avgPrices = data ? data.map((d) => d.avg_price ?? 0) : [];

  let currentMonthPriceChange = 0;
  if (avgPrices.length > 1) {
    const [prevMonthVal, currentMonthVal] = avgPrices.slice(-2);
    const diff = currentMonthVal - prevMonthVal;
    currentMonthPriceChange = (diff / prevMonthVal) * 100;
  }

  const avgOfAverages = avgPrices.length
    ? avgPrices.reduce((sum, curr) => sum + curr) / avgPrices.length
    : 0;

  const chartTabs: IChartTab[] = [
    {
      tabLabel: 'Price change',
      type: 'priceChanges',
      tabValue: `${formatNumberWithSign(currentMonthPriceChange)}%`,
      chartYValues: avgPrices.map((currentVal, index) => {
        if (index === 0) return 0;
        const prevVal = avgPrices[index - 1];
        if (prevVal <= 0) return 100;
        return ((currentVal - prevVal) / prevVal) * 100;
      }),
      legendLabel: 'Average price change (%)',
    },
    {
      tabLabel: 'Average price',
      type: 'avgPrices',
      tabValue: `${avgOfAverages.toFixed(2)} ${currency}`,
      chartYValues: avgPrices,
      legendLabel: 'Average prices',
    },
  ];
  const [activeChartType, setActiveChartType] =
    useState<IChartTab['type']>('avgPrices');

  const activeChart = chartTabs.find((c) => c.type == activeChartType);

  const sharedChartOptions = {
    chart: {
      type: 'polarArea',
    },
    grid: {
      borderColor: '#ddd5',
      strokeDashArray: 0,
    },

    yaxis: {
      labels: {
        formatter(value: any) {
          return value.toFixed(0);
        },
        style: {
          colors: '#A3A3A3',
          fontSize: '10px',
        },
      },
    },

    xaxis: {
      categories: xAxisLabels,
      labels: {
        style: {
          colors: ['#64748b'],
          fontSize: '12px',
        },
      },
    },
  };

  const chartOptions: {
    [K in IChartTab['type']]: any;
  } = {
    avgPrices: {
      chart: {
        type: 'area',
      },

      colors: ['#6A66B6'],
      stroke: {
        curve: 'straight',
      },
      annotations: {
        yaxis: [
          {
            y: avgOfAverages,
            borderColor: '#4375F3',
            strokeDashArray: 6,
            label: {
              borderColor: '#0000',
              position: 'left',
              offsetX: 46,
              style: {
                color: '#4375F3',
                background: '#0000',
                fontSize: '.75rem',
              },
              text: 'Average',
            },
          },
        ],
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${value.toFixed(2)} ${currency}`,
          title: {
            formatter: () => activeFilters.cuisine,
          },
        },
      },
    },

    priceChanges: {
      chart: {
        type: 'line',
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      colors: ['#60A5FA'],
      tooltip: {
        y: {
          formatter: (value: number) => `${value.toFixed(1)}%`,
          title: {
            formatter: () => 'Price Change',
          },
        },
      },
    },
  };

  return (
    <>
      <Card
        key={`${activeFilters.brandName}${activeFilters.cuisine}`}
        className={`${className} !py-0 h-full overflow-hidden`}
      >
        <CardHeader className="pl-4 !pr-0 !py-0 ">
          <div className="flex">
            <div className="py-4">
              <CardTitle>Average price</CardTitle>
              <CardSubTitle>
                {/* <span className="text-primary text-xs font-medium">
                  {activeFilters.brandName}
                </span>
                <span className="mx-0.5 text-xs font-medium">vs</span> */}
                <span className="text-primary text-xs font-medium">
                  {activeFilters.cuisine} cuisine
                </span>{' '}
                average
              </CardSubTitle>
            </div>
            <div className="flex ml-auto">
              {chartTabs.map((t) => (
                <div
                  onClick={() => {
                    setActiveChartType(t.type);
                  }}
                  key={t.type}
                  className={cn(
                    'cursor-pointer h-full border-l border-border flex flex-col justify-center gap-1 p-4 hover:opacity-75',
                    {
                      'bg-quarternary hover:opacity-100':
                        activeChartType === t.type,
                    },
                  )}
                >
                  <Typography className="text-quarternary text-xs font-medium">
                    {t.tabLabel}
                  </Typography>
                  <Typography className="text-content text-xl font-bold">
                    {t.tabValue}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="avg-prices-chart">
          <Chart
            type={chartOptions[activeChartType]?.chart?.type}
            className="animate-fade-in"
            key={activeChartType}
            height={214}
            series={[
              {
                name: activeFilters.brandName,
                data: activeChart?.chartYValues,
              },
            ]}
            options={useChart({
              ...chartOptions[activeChartType],
              ...sharedChartOptions,
            })}
          />
          <Typography className="text-xs font-medium text-center">
            {activeChart?.legendLabel}
          </Typography>
        </CardContent>
      </Card>
      <style>
        {`.avg-prices-chart .apexcharts-xaxis-texts-g text tspan:first-of-type {
          fill: #A3A3A3 !important;
          font-size:10px;
          font-weight: 400;`}
      </style>
    </>
  );
}
